// Store-specific behaviour layered on top of Dawn: cart timer, scroll-to-top,
// sticky add-to-cart, delivery date estimates and simple carousels.

if (!customElements.get('cart-timer')) {
  // Countdown shared across drawer re-renders; restarts once it runs out.
  customElements.define(
    'cart-timer',
    class CartTimer extends HTMLElement {
      static storageKey = 'sleecs-cart-timer-end';

      connectedCallback() {
        this.clock = this.querySelector('.cart-timer__clock');
        this.duration = Number(this.dataset.duration || 300) * 1000;
        this.tick();
        this.interval = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.interval);
      }

      endTime() {
        let end;
        try {
          end = Number(sessionStorage.getItem(CartTimer.storageKey));
        } catch (e) {}
        if (!end || end <= Date.now()) {
          end = Date.now() + this.duration;
          try {
            sessionStorage.setItem(CartTimer.storageKey, end);
          } catch (e) {}
        }
        return end;
      }

      tick() {
        if (!this.clock) return;
        const remaining = Math.max(0, Math.round((this.endTime() - Date.now()) / 1000));
        const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
        const seconds = String(remaining % 60).padStart(2, '0');
        this.clock.textContent = `${minutes}:${seconds}`;
      }
    }
  );
}

// Theme editor only: keep the cart drawer open so cart settings can be previewed live.
if (window.Shopify && Shopify.designMode) {
  const openCartPreview = () => {
    const drawer = document.querySelector('cart-drawer[data-editor-preview]');
    if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('active')) drawer.open();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', openCartPreview);
  else openCartPreview();
  document.addEventListener('shopify:section:load', openCartPreview);
}

if (!customElements.get('scroll-to-top')) {
  customElements.define(
    'scroll-to-top',
    class ScrollToTop extends HTMLElement {
      connectedCallback() {
        this.offset = Number(this.dataset.offset || 400);
        this.onScroll = () => (this.hidden = window.scrollY < this.offset);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
        this.querySelector('button').addEventListener('click', () =>
          window.scrollTo({ top: 0, behavior: 'smooth' })
        );
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
      }
    }
  );
}

if (!customElements.get('delivery-dates')) {
  // Fills [data-min-days]/[data-max-days] steps with dates relative to today.
  customElements.define(
    'delivery-dates',
    class DeliveryDates extends HTMLElement {
      connectedCallback() {
        const months = (this.dataset.months || '').split(',').map((m) => m.trim());
        const days = (this.dataset.days || '').split(',').map((d) => d.trim());
        const format = this.dataset.format || 'mm_dd';

        const label = (offset) => {
          const date = new Date();
          date.setDate(date.getDate() + offset);
          const month = months[date.getMonth()] || date.toLocaleString(undefined, { month: 'short' });
          const weekday = days[(date.getDay() + 6) % 7] || '';
          if (format === 'dd_mm') return `${date.getDate()} ${month}`;
          if (format === 'day_dd_mm') return `${weekday}, ${date.getDate()} ${month}`;
          if (format === 'day_mm_dd') return `${weekday}, ${month} ${date.getDate()}`;
          return `${month} ${date.getDate()}`;
        };

        this.querySelectorAll('[data-min-days]').forEach((step) => {
          const min = Number(step.dataset.minDays);
          const max = Number(step.dataset.maxDays);
          step.querySelectorAll('[data-start-date]').forEach((el) => (el.textContent = label(min)));
          step.querySelectorAll('[data-end-date]').forEach((el) => (el.textContent = label(max)));
        });
      }
    }
  );
}

if (!customElements.get('sticky-atc')) {
  // Shows once the main add-to-cart button scrolls out of view and drives the main product form.
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        // Ancestors with a transform (Dawn's scroll-in animation) turn position: fixed into
        // "fixed to that ancestor", so the bar must live directly under <body>.
        if (this.parentElement !== document.body) {
          document
            .querySelectorAll(`body > sticky-atc[data-section="${this.dataset.section}"]`)
            .forEach((stale) => stale.remove());
          document.body.appendChild(this);
          return; // Moving re-runs connectedCallback from the new position.
        }

        this.mainButton = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
        this.mainForm = document.getElementById(`product-form-${this.dataset.section}`);
        this.select = this.querySelector('select');
        this.button = this.querySelector('[data-sticky-submit]');

        if (this.mainButton) {
          this.observer = new IntersectionObserver(([entry]) => {
            const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('sticky-atc--visible', passed);
            document.body.classList.toggle('sticky-atc-open', passed);
          });
          this.observer.observe(this.mainButton);
        }

        if (this.listening) return;
        this.listening = true;

        this.button?.addEventListener('click', () => this.submit());
        this.select?.addEventListener('change', () => this.syncPrice());

        // Theme editor: show the bar while its block is selected.
        this.addEventListener('shopify:block:select', () => this.classList.add('sticky-atc--visible'));
        this.addEventListener('shopify:block:deselect', () => this.classList.remove('sticky-atc--visible'));

        this.onVariantChange = (event) => {
          const variant = event.data?.variant;
          if (!variant || !this.select) return;
          this.select.value = variant.id;
          this.syncPrice();
        };
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.unsubscribe?.();
      }

      syncPrice() {
        const option = this.select?.selectedOptions[0];
        if (!option) return;
        const price = this.querySelector('[data-sticky-price]');
        const compare = this.querySelector('[data-sticky-compare]');
        if (price) price.textContent = option.dataset.price;
        if (compare) {
          compare.textContent = option.dataset.compare || '';
          compare.hidden = !option.dataset.compare;
        }
        if (this.button) {
          const available = option.dataset.available === 'true';
          this.button.disabled = !available;
          this.button.querySelector('span').textContent = available
            ? this.dataset.label
            : window.variantStrings.soldOut;
        }
      }

      submit() {
        if (!this.mainForm) return;
        const idInput = this.mainForm.querySelector('[name="id"]');
        if (this.select && idInput && idInput.value !== this.select.value) {
          // A variant chosen in the sticky bar overrides the main picker.
          idInput.value = this.select.value;
        }
        if (this.dataset.function === 'scroll') {
          this.mainForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.mainButton?.click();
      }
    }
  );
}

if (!customElements.get('product-video-autoplay')) {
  // Loads and plays Shopify-hosted product videos when they're on screen instead of waiting
  // for the play button. Browsers only allow sound after the visitor interacts with the page,
  // so if sound is blocked the video plays muted and unmutes on the first tap/click/key press.
  customElements.define(
    'product-video-autoplay',
    class ProductVideoAutoplay extends HTMLElement {
      connectedCallback() {
        const gallery = document.getElementById(this.dataset.gallery);
        if (!gallery) return;
        this.withSound = this.dataset.sound === 'true';

        const players = Array.from(gallery.querySelectorAll('deferred-media')).filter((player) =>
          player.querySelector('template')?.content.querySelector('video')
        );
        if (!players.length) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.start(entry.target);
              else entry.target.querySelector('video')?.pause();
            });
          },
          { threshold: 0.6 }
        );
        players.forEach((player) => this.observer.observe(player));
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.removeUnmuteListeners();
      }

      start(player) {
        let video = player.querySelector('video');
        const firstStart = !video;
        if (firstStart) {
          if (typeof player.loadContent !== 'function') return;
          player.loadContent(false);
          video = player.querySelector('video');
          if (!video) return;
          video.playsInline = true;
          video.muted = !this.withSound;
        }

        video.play().catch(() => {
          // Sound was blocked: play muted now, turn sound on at the first interaction.
          video.muted = true;
          video.play().catch(() => {});
          if (this.withSound && firstStart) this.unmuteOnInteraction(video);
        });
      }

      unmuteOnInteraction(video) {
        this.removeUnmuteListeners();
        this.unmute = () => {
          this.removeUnmuteListeners();
          video.muted = false;
          if (video.paused) video.play().catch(() => {});
        };
        ['pointerdown', 'keydown', 'touchstart'].forEach((type) =>
          document.addEventListener(type, this.unmute, { capture: true, passive: true })
        );
      }

      removeUnmuteListeners() {
        if (!this.unmute) return;
        ['pointerdown', 'keydown', 'touchstart'].forEach((type) =>
          document.removeEventListener(type, this.unmute, { capture: true })
        );
        this.unmute = null;
      }
    }
  );
}

if (!customElements.get('sleecs-carousel')) {
  // Lightweight scroll-snap carousel with arrows and dots (used by the custom content sections).
  customElements.define(
    'sleecs-carousel',
    class SleecsCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = Array.from(this.track?.children || []);
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
        this.track?.addEventListener('scroll', () => this.updateDots(), { passive: true });

        const autoplay = Number(this.dataset.autoplay || 0);
        if (autoplay > 0) {
          this.timer = setInterval(() => this.step(1, true), autoplay * 1000);
          this.addEventListener('pointerenter', () => clearInterval(this.timer));
        }
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      currentIndex() {
        if (!this.track || !this.slides.length) return 0;
        const left = this.track.scrollLeft;
        let closest = 0;
        this.slides.forEach((slide, index) => {
          if (Math.abs(slide.offsetLeft - this.track.offsetLeft - left) <
            Math.abs(this.slides[closest].offsetLeft - this.track.offsetLeft - left)) {
            closest = index;
          }
        });
        return closest;
      }

      goTo(index) {
        const slide = this.slides[index];
        if (!slide) return;
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }

      step(direction, wrap = false) {
        const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
        let next = this.currentIndex() + direction;
        if (direction > 0 && atEnd) next = wrap || this.dataset.loop === 'true' ? 0 : this.slides.length - 1;
        if (next < 0) next = this.dataset.loop === 'true' ? this.slides.length - 1 : 0;
        this.goTo(next);
      }

      updateDots() {
        const index = this.currentIndex();
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
      }
    }
  );
}
